import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";

interface InvestorProfileSettingsProps {
  riskTolerance?: string;
  portfolioSize?: string;
  investmentExperience?: string;
  investmentGoals?: string[];
  sectors?: string[];
  preferredResearchStyles?: string[];
}

export function InvestorProfileSettings({
  riskTolerance,
  portfolioSize,
  investmentExperience,
  investmentGoals,
  sectors,
}: InvestorProfileSettingsProps) {
  const getRiskToleranceLabel = (value: string) => {
    const labels: Record<string, string> = {
      conservative: "Conservative - Minimize risk",
      moderate: "Moderate - Balanced approach",
      aggressive: "Aggressive - Maximize returns",
    };
    return labels[value] || value;
  };

  const getPortfolioSizeLabel = (value: string) => {
    const labels: Record<string, string> = {
      "under-50k": "Under $50,000",
      "50k-250k": "$50,000 - $250,000",
      "250k-1m": "$250,000 - $1,000,000",
      "1m-5m": "$1,000,000 - $5,000,000",
      "over-5m": "Over $5,000,000",
    };
    return labels[value] || value;
  };

  const getExperienceLabel = (value: string) => {
    const labels: Record<string, string> = {
      beginner: "Beginner (0-2 years)",
      intermediate: "Intermediate (3-5 years)",
      experienced: "Experienced (6-10 years)",
      expert: "Expert (10+ years)",
    };
    return labels[value] || value;
  };

  return (
    <div className="space-y-6">
      {/* Investment Goals */}
      <Card>
        <CardHeader>
          <CardTitle>Investment Goals</CardTitle>
          <CardDescription>
            Select all investment goals that apply to your portfolio
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {investmentGoals && investmentGoals.length > 0 ? (
              investmentGoals.map((goal) => (
                <Badge key={goal} variant="secondary">
                  {goal}
                </Badge>
              ))
            ) : (
              <p className="text-sm text-muted-foreground">No investment goals selected</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Risk Tolerance */}
      <Card>
        <CardHeader>
          <CardTitle>Risk Tolerance</CardTitle>
          <CardDescription>
            Your approach to investment risk and volatility
          </CardDescription>
        </CardHeader>
        <CardContent>
          {riskTolerance ? (
            <Badge variant="outline" className="text-base py-2 px-4">
              {getRiskToleranceLabel(riskTolerance)}
            </Badge>
          ) : (
            <p className="text-sm text-muted-foreground">Risk tolerance not set</p>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Portfolio Size */}
        <Card>
          <CardHeader>
            <CardTitle>Portfolio Size</CardTitle>
            <CardDescription>Current portfolio value range</CardDescription>
          </CardHeader>
          <CardContent>
            {portfolioSize ? (
              <Badge variant="outline" className="text-base py-2 px-4">
                {getPortfolioSizeLabel(portfolioSize)}
              </Badge>
            ) : (
              <p className="text-sm text-muted-foreground">Portfolio size not set</p>
            )}
          </CardContent>
        </Card>

        {/* Investment Experience */}
        <Card>
          <CardHeader>
            <CardTitle>Investment Experience</CardTitle>
            <CardDescription>Your level of investment expertise</CardDescription>
          </CardHeader>
          <CardContent>
            {investmentExperience ? (
              <Badge variant="outline" className="text-base py-2 px-4">
                {getExperienceLabel(investmentExperience)}
              </Badge>
            ) : (
              <p className="text-sm text-muted-foreground">Experience level not set</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Sectors of Interest */}
      <Card>
        <CardHeader>
          <CardTitle>Sectors of Interest</CardTitle>
          <CardDescription>
            Industry sectors you're interested in for investment
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {sectors && sectors.length > 0 ? (
              sectors.map((sector) => (
                <Badge key={sector} variant="outline">
                  {sector}
                </Badge>
              ))
            ) : (
              <p className="text-sm text-muted-foreground">No sectors selected</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
